// Basic
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
//Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import actionsOrganizations from '../../../api/actionsOrganizations';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Input } from '@/common-utils/components/ui/input';
import { Button } from '@/common-utils/components/ui/button';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
import organizationFields from '../../../fields/organizationFields';
import ICONS from '@/common-utils/enums/icons';
import placeholder from 'url:../../../../../../../resource/images/logo1.png';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.organization
 * @param root0.setLaunchRefetch
 * @returns {JSX} ModalUpdateOrganizationBrand
 */
function ModalUpdateOrganizationBrand({ isOpen, setIsOpen, organization, setLaunchRefetch }) {
    // Toast
    const { toast } = useToast();

    // State
    const [file, setFile] = useState(null);
    const [shouldAcceptFile, setShouldAcceptFile] = useState(true);
    const [logo, setLogo] = useState(null);
    const [fileSelectionKey, setFileSelectionKey] = useState(0);
    const [mainColor, setMainColor] = useState(organization?.brand?.style?.mainColor);
    const [imageError, setImageError] = useState(false);
    const [colorError, setColorError] = useState(false);

    // Get logoPath exactly like original
    const logoPath = organization?.brand?.style?.assets?.logoPath;

    // Memoize image source to prevent flickering
    const imageSrc = useMemo(() => {
        if (file) {
            return file;
        }
        if (logoPath && !imageError) {
            return logoPath;
        }
        // Always return placeholder when no file and no valid logoPath
        return placeholder;
    }, [file, logoPath, imageError]);

    // Handle file change - exact same logic as original
    const handleChange = (event) => {
        const fileToSend = event.target.files[0];
        if (fileToSend && fileToSend.size / 1024 < 200) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setShouldAcceptFile(true);
                setFile(e.target.result);
                setLogo(e.target.result);
                setImageError(false);
            };
            reader.readAsDataURL(fileToSend);
        } else {
            setFile(null);
            setFileSelectionKey(prev => prev + 1);
            setShouldAcceptFile(false);
            setLogo(null);
        }
    };

    // Remove selected image - exact same logic as original
    const removeSelectedImage = () => {
        setFile(null);
        setFileSelectionKey(prev => prev + 1);
        setLogo(null);
        setImageError(false);
    };

    // Handle image error to prevent loops
    const handleImageError = () => {
        if (!imageError) {
            setImageError(true);
        }
    };

    // API Calls
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.update_brand.success' namespace={nsOrganization} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);  // Close modal even on error
        toast({
            description: <Entity entity='modal.update_brand.error.default' namespace={nsOrganization} />,
            variant: 'danger'
        });
    };
    const submitUpdateOrganizationBrand = actionsOrganizations.updateOrganizationBrand(onSuccess, onError);

    // Validate color field - must be exactly 7 characters (#XXXXXX) and not empty
    const isValidColor = mainColor && mainColor.trim() !== '' && mainColor.match(/^#[0-9A-Fa-f]{6}$/) && mainColor.length === 7;

    // Submit form - handle 3 cases with coherent brand structure
    const onSubmit = (e) => {
        e.preventDefault();

        // // Validate color before submitting
        if (!isValidColor) {
            setColorError(true);
            return;
        }

        // Clear any previous error
        setColorError(false);

        // Start with complete organization data
        const data = {
            ...organization
        };

        // Always rebuild brand structure to ensure coherence
        const brandData = {
            ...organization.brand,
            style: {
                ...organization.brand?.style,
                mainColor: mainColor  // Always include updated color
            }
        };

        // Only add assets if logo is being changed
        if (logo && shouldAcceptFile) {
            brandData.style.assets = {
                ...organization.brand?.style?.assets,  // Preserve existing assets
                logo: logo
            };

            // Preserve logoPath if it exists
            if (logoPath) {
                brandData.style.assets.logoPath = logoPath;
            }
        } else {
            // If only color changed, assets should be empty
            brandData.style.assets = {};
        }

        // Apply the coherent brand structure to organization
        data.brand = brandData;

        // Call API with complete organization data and coherent brand structure
        submitUpdateOrganizationBrand.mutate({
            uuid: organization[organizationFields.UUID.key],
            data: data
        });
    };

    // Check color validity only when user actively modifies the field
    // Error only shows when user creates an invalid state, not on initial load
    useEffect(() => {
        // Only validate if the modal is open and user has interacted with the field
        if (isOpen && mainColor !== (organization?.brand?.style?.mainColor || '#000000')) {
            // Show error if field is empty or invalid after user modification
            if (mainColor === '' || mainColor.trim() === '') {
                setColorError(true);
            } else {
                // Check if it's a valid hex color
                const isValid = mainColor.match(/^#[0-9A-Fa-f]{6}$/) && mainColor.length === 7;
                setColorError(!isValid);
            }
        }
    }, [mainColor, isOpen, organization]);

    // Reset form when modal opens - exact same logic as original
    useEffect(() => {
        if (isOpen) {
            setFile(null);
            setLogo(null);
            setShouldAcceptFile(true);
            setFileSelectionKey(0);
            setMainColor(organization?.brand?.style?.mainColor || '#000000');
            setImageError(false);
            setColorError(false); // Always start without error since valid color is always provided
        }
    }, [isOpen, organization]);

    if (!isOpen) {
        return null;
    }

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn('max-w-lg')}>
                <HeaderTitle title='modal.update_brand.title' namespace={nsOrganization} />
                <ModalBody>
                    <form onSubmit={onSubmit}>
                        <div className='mb-6'>
                            <div className='font-semibold mb-3'>
                                <Entity entity='keys.brand_logo' namespace={nsOrganization} className='font-bold' />
                            </div>
                            <div className='space-y-4'>
                                <div className='flex gap-4'>
                                    <div className='flex-1 text-sm text-gray-600'>
                                        <p className='mb-2'>
                                            <Entity entity='modal.update_brand.logo.info1' namespace={nsOrganization} />
                                        </p>
                                        <ul className='list-disc list-inside space-y-1'>
                                            <li>
                                                <Entity entity='modal.update_brand.logo.info2' namespace={nsOrganization} />
                                            </li>
                                            <li>
                                                <Entity entity='modal.update_brand.logo.info3' namespace={nsOrganization} />
                                            </li>
                                        </ul>
                                    </div>
                                    <div className='flex-shrink-0'>
                                        <img
                                            className='w-24 h-24 object-contain border rounded bg-gray-50'
                                            src={imageSrc}
                                            onError={handleImageError}
                                            alt='Logo preview'
                                            style={{ display: 'block' }}
                                        />
                                    </div>
                                </div>
                                <div className='flex gap-2'>
                                    <input
                                        type='file'
                                        onChange={handleChange}
                                        accept='.png,.jpg,.jpeg'
                                        key={fileSelectionKey}
                                        className='hidden'
                                        id='fileImageContents'
                                    />
                                    <Button
                                        type='button'
                                        variant='secondary'
                                        icon={ICONS.UPLOAD}
                                        entity='modal.update_brand.logo.upload'
                                        namespace={nsOrganization}
                                        onClick={() => document.getElementById('fileImageContents').click()}
                                    />
                                    {file && (
                                        <Button
                                            type='button'
                                            variant='danger-outline'
                                            icon={ICONS.DELETE}
                                            entity='modal.update_brand.logo.delete'
                                            namespace={nsOrganization}
                                            onClick={removeSelectedImage}
                                        />
                                    )}
                                </div>
                                {!shouldAcceptFile && (
                                    <div className='text-sm text-red-600 bg-red-50 p-2 rounded'>
                                        <Entity entity='modal.update_brand.logo.error' namespace={nsOrganization} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className='mb-6'>
                            <label className='block text-sm font-medium text-gray-700 mb-2'>
                                <Entity entity='keys.main_color' namespace={nsOrganization} className='font-bold' />
                            </label>
                            <div className='flex gap-2 items-center'>
                                <Input
                                    type='color'
                                    value={mainColor}
                                    onChange={(e) => {
                                        setMainColor(e.target.value);
                                        setColorError(false);
                                    }}
                                    className={cn('w-16 h-10 p-1 cursor-pointer', colorError && 'border-red-500 focus:border-red-500')}
                                />
                                <Input
                                    type='text'
                                    value={mainColor}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        // Ensure it starts with # and is a valid hex format
                                        if (value.startsWith('#') || value === '') {
                                            setMainColor(value);
                                        } else if (value.match(/^[0-9A-Fa-f]{1,6}$/)) {
                                            setMainColor(`#${value}`);
                                        }
                                        setColorError(false);
                                    }}
                                    placeholder='#000000'
                                    className={cn('flex-1', colorError && 'border-red-500 focus:border-red-500')}
                                    maxLength={7}
                                />
                            </div>
                            {colorError && (
                                <div className='text-sm text-red-600 bg-red-50 p-2 rounded mt-2'>
                                    <Entity entity='modal.update_brand.color.error' namespace={nsOrganization} />
                                </div>
                            )}
                        </div>
                    </form>
                </ModalBody>
                <FooterForm
                    onSubmit={onSubmit}
                    isSubmitting={submitUpdateOrganizationBrand.isLoading}
                    isSubmitDisabled={!shouldAcceptFile || !isValidColor || !!submitUpdateOrganizationBrand.isLoading}
                />
            </ModalContent>
        </Modal>
    );
}

ModalUpdateOrganizationBrand.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    organization: PropTypes.object.isRequired,
    setLaunchRefetch: PropTypes.func
};

export default ModalUpdateOrganizationBrand;